'use client';

import { useState, useMemo, memo, useCallback } from 'react';
import { cx } from '@/utils/cx';
import type { NavItemDividerType, NavItemType } from '../config';
import { NavItemBase } from './nav-item';

interface NavListProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** Additional CSS classes to apply to the list. */
    className?: string;
    /** List of items to display. */
    items: (NavItemType | NavItemDividerType)[];
}

export const NavList = memo(({ activeUrl, items, className }: NavListProps) => {
    const [open, setOpen] = useState(false);

    // Memoize expensive active item lookup
    const activeItem = useMemo(() =>
        items.find((item) => item.href === activeUrl || item.items?.some((subItem) => subItem.href === activeUrl)),
        [items, activeUrl],
    );

    const [currentItem, setCurrentItem] = useState(activeItem);

    // Memoize toggle handler to prevent recreation on every render
    const handleToggle = useCallback((e: React.FormEvent<HTMLDetailsElement>, item: NavItemType) => {
        setOpen(e.currentTarget.open);
        setCurrentItem(item);
    }, []);

    return (
        <ul className={cx('mt-4 flex flex-col px-2 lg:px-3', className)}>
            {items.map((item, index) => {
                if (item.divider) {
                    return (
                        <li key={index} className="w-full px-0.5 py-2">
                            <hr className="h-px w-full border-none bg-border-secondary" />
                        </li>
                    );
                }

                if (item.items?.length) {
                    return (
                        <details
                            key={item.label}
                            open={activeItem?.href === item.href}
                            className="appearance-none py-0.5"
                            onToggle={(e) => handleToggle(e, item)}
                        >
                            <NavItemBase href={item.href} badge={item.badge} icon={item.icon} type="collapsible">
                                {item.label}
                            </NavItemBase>

                            <dd>
                                <ul className="py-0.5">
                                    {item.items.map((childItem) => (
                                        <li key={childItem.label} className="py-0.5">
                                            <NavItemBase
                                                href={childItem.href}
                                                badge={childItem.badge}
                                                type="collapsible-child"
                                                current={activeUrl === childItem.href}
                                            >
                                                {childItem.label}
                                            </NavItemBase>
                                        </li>
                                    ))}
                                </ul>
                            </dd>
                        </details>
                    );
                }

                return (
                    <li key={item.label} className="py-0.5">
                        <NavItemBase
                            type="link"
                            badge={item.badge}
                            icon={item.icon}
                            href={item.href}
                            current={currentItem?.href === item.href}
                            open={open && currentItem?.href === item.href}
                            disabled={(item as NavItemType).disabled}
                        >
                            {item.label}
                        </NavItemBase>
                    </li>
                );
            })}
        </ul>
    );
});
